import { ButtonLink } from "design-system/atoms/Button"
import { ArrowUpRight, FileText, Globe, MessagesSquare, TextAlignStart } from "lucide-react"
import { useConversationMessageKnowledgeBaseItemsQuery } from "modules/conversations/hooks/useConversationMessageKnowledgeBaseItemsQuery"
import { Loading } from "modules/shared/components/Loading"
import { useTranslation } from "react-i18next"
import { KnowledgeBaseItemKind } from "schema"

type Props = {
  messageId: string
}

const KnoledgeBaseIcon = ({ kind, size }: { kind?: KnowledgeBaseItemKind; size: number }) => {
  if (!kind) return
  switch (kind) {
    case KnowledgeBaseItemKind.QuestionAndAnswer:
      return <MessagesSquare size={size} />
    case KnowledgeBaseItemKind.Text:
      return <TextAlignStart size={size} />
    case KnowledgeBaseItemKind.File:
      return <FileText size={size} />
    case KnowledgeBaseItemKind.Website:
      return <Globe size={size} />
  }
}

export const MessageKnowledgeBaseItems = ({ messageId }: Props) => {
  const { t } = useTranslation()
  const { data, isPending } = useConversationMessageKnowledgeBaseItemsQuery(messageId)

  if (isPending) {
    return <Loading />
  }

  if (!data) {
    return (
      <span className="text-sm text-muted-foreground">
        {t("conversations.chat.knowledgeBaseItems.noData")}
      </span>
    )
  }

  const knowledgeBaseReferences = data.filter((reference) => reference.source === "KnowledgeBase")
  const generalKnowledgeBase = knowledgeBaseReferences.filter(
    (reference) => reference.knowledgeBaseItem && !reference.knowledgeBaseItem.campaignId,
  )
  const fromCampaignKnowledgeBase = knowledgeBaseReferences.filter(
    (reference) => reference.knowledgeBaseItem && reference.knowledgeBaseItem.campaignId,
  )

  const info = [
    {
      data: generalKnowledgeBase,
      title: t("conversations.chat.knowledgeBaseItems.general"),
    },
    {
      data: fromCampaignKnowledgeBase,
      title: t("conversations.chat.knowledgeBaseItems.fromCampaign"),
    },
  ].filter((e) => e.data.length)

  return (
    <div className="flex flex-col gap-4">
      {!info.length && (
        <span className="text-sm text-muted-foreground">
          {t("conversations.chat.knowledgeBaseItems.noData")}
        </span>
      )}
      {info.map((element) => (
        <div key={element.title} className="flex flex-col gap-2">
          <span className="text-sm text-muted-foreground">{element.title}</span>
          <div className="flex flex-col">
            {element.data.map((item) => (
              <ButtonLink
                variant="ghost"
                to={"/knowledge/knowledge-base"}
                search={{
                  section: item.knowledgeBaseItem?.kind,
                  highlightId: item.knowledgeBaseItemId,
                }}
                key={item.knowledgeBaseItemId}
                className="flex gap-2 items-center justify-start w-full"
                target="_blank"
              >
                <div className="flex gap-2 items-center w-full">
                  <KnoledgeBaseIcon kind={item.knowledgeBaseItem?.kind} size={14} />
                  {t(`conversations.chat.knowledgeBaseItems.item.${item.knowledgeBaseItem?.kind}`)}
                </div>

                <ArrowUpRight size={12} />
              </ButtonLink>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
